import { useRef, useState, useEffect, useCallback } from "react";
import { Row, Col, Button } from "react-bootstrap";
import StopsUnitForm from "../header-compoents/StopsUnitForm";
import ScheduleTable from "./ScheduleTable";

/**
 * Provide horizontal scrolling controls for ScheduleTable.
 */
export default function TableContainer() {
  const wrapperRef = useRef(null);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  /**
   * Locate the current scroll container.
   */
  const getScrollEl = useCallback(() => {
    return wrapperRef.current?.querySelector(".table-responsive") || null;
  }, []);

  /**
   * Measure the sticky first column.
   */
  const getStickyFirstColWidth = (scrollEl) => {
    const stickyCell = scrollEl.querySelector("table tbody td:first-child");

    if (!stickyCell) return 0;

    // Use getBoundingClientRect to include borders; offsetWidth is also fine.
    return stickyCell.getBoundingClientRect().width || 0;
  };

  /**
   * Compute horizontal scroll per click.
   */
  const getPageScrollAmount = (scrollEl) => {
    const stickyWidth = getStickyFirstColWidth(scrollEl);

    // The scrollable viewport (excluding sticky col)
    const amount = scrollEl.clientWidth - (stickyWidth + stickyWidth * 0.2);

    return Math.max(amount, 0);
  };

  /**
   * Recompute enabled/disabled state.
   */
  const updateScrollState = useCallback(() => {
    const scrollEl = getScrollEl();
    if (!scrollEl) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollEl;

    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);

    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft < maxScrollLeft - 1);
  }, [getScrollEl]);

  /**
   * Scroll left/right one "page" of content.
   */
  const scrollByPage = (direction) => {
    const scrollEl = getScrollEl();
    if (!scrollEl) return;

    const page = getPageScrollAmount(scrollEl);
    if (page <= 0) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollEl;
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);

    // Direction: -1 (left), +1 (right)
    const desired = scrollLeft + direction * page;

    // Clamp so we never overshoot
    const next = Math.min(Math.max(desired, 0), maxScrollLeft);

    scrollEl.scrollTo({ left: next, behavior: "smooth" });
  };

  /**
   * Sync state after table layout changes.
   */
  useEffect(() => {
    requestAnimationFrame(updateScrollState);
  }, [layoutVersion, updateScrollState]);

  /**
   * Listen for scroll events via capture on a stable ancestor.
   */
  useEffect(() => {
    const wrapperEl = wrapperRef.current;
    if (!wrapperEl) return;

    const onScrollCapture = (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.classList.contains("table-responsive")) return;
      updateScrollState();
    };

    wrapperEl.addEventListener("scroll", onScrollCapture, { capture: true });
    return () => {
      wrapperEl.removeEventListener("scroll", onScrollCapture, {
        capture: true,
      });
    };
  }, [updateScrollState]);

  /**
   * Keep state correct on window resize.
   */
  useEffect(() => {
    const onResize = () => requestAnimationFrame(updateScrollState);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [updateScrollState]);


  return (
    <Row ref={wrapperRef} className="table-sticky-wrapper">
      <Col className="d-flex table-scroll-controls">
        <StopsUnitForm />
        <div className="me-auto" />

        <Button
          disabled={!canScrollLeft}
          aria-label="Scroll table left"
          onClick={() => scrollByPage(-1)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: canScrollLeft ? "pointer" : "default",
            opacity: canScrollLeft ? 1 : 0.3,
          }}
        >
          <i className="brand-blue fas fa-arrow-circle-left fa-3x" />
        </Button>

        <Button
          disabled={!canScrollRight}
          aria-label="Scroll table right"
          onClick={() => scrollByPage(1)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: canScrollRight ? "pointer" : "default",
            opacity: canScrollRight ? 1 : 0.3,
          }}
        >
          <i className="brand-blue fas fa-arrow-circle-right fa-3x" />
        </Button>
      </Col>

      <ScheduleTable onLayoutChange={() => setLayoutVersion((v) => v + 1)} />
    </Row>
  );
}
